import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, PiggyBank, Receipt } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";

const propertyTypes = [
  { id: "single", label: "Single Family", mult: 1.0 },
  { id: "condo", label: "Condo", mult: 0.85 },
  { id: "townhome", label: "Townhome", mult: 0.9 },
  { id: "multi", label: "Multi-Family", mult: 1.35 },
];

const ages = [
  { id: "new", label: "< 10 yrs", mult: 1.0 },
  { id: "mid", label: "10–25 yrs", mult: 1.05 },
  { id: "old", label: "25–50 yrs", mult: 1.15 },
  { id: "legacy", label: "50+ yrs", mult: 1.25 },
];

const bundles = [
  { id: "prepurchase", label: "Pre-Purchase Comprehensive", price: null, base: true },
  { id: "fourpoint", label: "4-Point Insurance", price: 175 },
  { id: "windmit", label: "Wind Mitigation Discount", price: 150 },
  { id: "thermal", label: "Infrared Thermal Scan", price: 95 },
  { id: "annual", label: "Annual Maintenance Inspection", price: 325 },
  { id: "energy", label: "Energy Loss Audit", price: 275 },
  { id: "newconstruction", label: "New Construction", price: 375 },
  { id: "prelisting", label: "Pre-Listing Inspection", price: 350 },
];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all duration-300 ${
      active
        ? "bg-obsidian text-sand border-obsidian"
        : "bg-transparent text-slate-600 border-sandline hover:border-obsidian/40"
    }`}
  >
    {children}
  </button>
);

export const Calculator = ({ onBook }) => {
  const [sqft, setSqft] = useState(2200);
  const [ptype, setPtype] = useState("single");
  const [age, setAge] = useState("mid");
  const [selected, setSelected] = useState(["prepurchase", "windmit"]);

  const estimate = useMemo(() => {
    const pMult = propertyTypes.find((p) => p.id === ptype).mult;
    const aMult = ages.find((a) => a.id === age).mult;
    let total = 0;
    if (selected.includes("prepurchase")) total += (275 + sqft * 0.1) * pMult * aMult;
    bundles.filter((b) => b.price).forEach((b) => {
      if (selected.includes(b.id)) total += b.price;
    });
    return Math.max(150, Math.round(total / 5) * 5);
  }, [sqft, ptype, age, selected]);

  const savings = useMemo(() => {
    if (!selected.includes("windmit")) return null;
    const low = Math.round((500 + sqft * 0.1) / 50) * 50;
    const high = Math.min(2200, Math.round((900 + sqft * 0.45) / 50) * 50);
    return { low, high };
  }, [selected, sqft]);

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id]));

  const serviceLabel = bundles.filter((b) => selected.includes(b.id)).map((b) => b.label).join(" + ") || "Custom";

  return (
    <section id="calculator" className="relative bg-obsidian noise py-28 sm:py-36 overflow-hidden">
      <div className="absolute inset-0 blueprint-grid" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14">
          <div className="font-mono text-xs tracking-[0.25em] text-sun uppercase font-semibold mb-4">
            / Cost Estimator
          </div>
          <h2 className="font-display font-bold tracking-tight text-sand text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
            Know your inspection cost before you call.
          </h2>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8" data-testid="pricing-calculator-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="bg-deep/60 backdrop-blur border border-sand/10 rounded-2xl p-7 sm:p-10 space-y-10"
          >
            <div>
              <div className="flex items-baseline justify-between mb-5">
                <label className="font-mono text-[11px] tracking-[0.25em] text-sand/50 uppercase">Square Footage</label>
                <span className="font-display font-extrabold text-sand text-2xl" data-testid="calculator-sqft-value">
                  {sqft.toLocaleString()} <span className="text-sm text-sand/50 font-medium">sq ft</span>
                </span>
              </div>
              <Slider
                data-testid="calculator-sqft-slider"
                value={[sqft]}
                min={800}
                max={6000}
                step={50}
                onValueChange={(v) => setSqft(v[0])}
              />
              <div className="flex justify-between mt-2 font-mono text-[10px] text-sand/30 tracking-widest">
                <span>800</span><span>6,000+</span>
              </div>
            </div>

            <div>
              <label className="block font-mono text-[11px] tracking-[0.25em] text-sand/50 uppercase mb-4">Property Type</label>
              <div className="flex flex-wrap gap-2.5">
                {propertyTypes.map((p) => (
                  <Chip key={p.id} testid={`calculator-ptype-${p.id}`} active={ptype === p.id} onClick={() => setPtype(p.id)}>
                    <span className={ptype === p.id ? "" : "text-inherit"}>{p.label}</span>
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[11px] tracking-[0.25em] text-sand/50 uppercase mb-4">Home Age</label>
              <div className="flex flex-wrap gap-2.5">
                {ages.map((a) => (
                  <Chip key={a.id} testid={`calculator-age-${a.id}`} active={age === a.id} onClick={() => setAge(a.id)}>
                    {a.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[11px] tracking-[0.25em] text-sand/50 uppercase mb-4">Service Bundle</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {bundles.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`calculator-bundle-${b.id}`}
                    onClick={() => toggle(b.id)}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all duration-300 ${
                      selected.includes(b.id)
                        ? "border-sun bg-sun/10 text-sand"
                        : "border-sand/10 text-sand/50 hover:border-sand/30"
                    }`}
                  >
                    <Checkbox checked={selected.includes(b.id)} className="pointer-events-none border-sand/40 data-[state=checked]:bg-sun data-[state=checked]:border-sun" />
                    <span className="text-sm font-medium">{b.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="bg-sand rounded-2xl p-7 sm:p-10 flex flex-col"
          >
            <div className="font-mono text-[11px] tracking-[0.25em] text-obsidian/50 uppercase mb-8">Live Estimate</div>

            <div className="flex items-start gap-3">
              <Receipt size={20} className="text-terra mt-1.5 shrink-0" />
              <div>
                <div className="text-sm text-slate-500">Estimated Inspection Fee</div>
                <div className="font-display font-black text-obsidian text-5xl tracking-tight" data-testid="calculator-estimated-price">
                  ${estimate.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-5">
              <div className="flex items-center gap-3 border-t border-sandline pt-5">
                <PiggyBank size={18} className="text-flteal shrink-0" />
                <div>
                  <div className="text-sm text-slate-500">Potential FL Insurance Savings</div>
                  <div className="font-display font-bold text-obsidian text-xl" data-testid="calculator-savings">
                    {savings ? `$${savings.low.toLocaleString()} – $${savings.high.toLocaleString()} / yr` : "Add Wind Mitigation"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 border-t border-sandline pt-5">
                <Clock size={18} className="text-sun shrink-0" />
                <div>
                  <div className="text-sm text-slate-500">Report Turnaround</div>
                  <div className="font-display font-bold text-obsidian text-xl">Same Day / 24 hrs</div>
                </div>
              </div>
            </div>

            <button
              data-testid="calculator-book-quote-button"
              onClick={() => onBook({ service: serviceLabel, sqft, quote: estimate })}
              className="group mt-auto pt-10"
            >
              <span className="flex items-center justify-center gap-3 bg-sun text-obsidian font-semibold px-7 py-4 rounded-full transition-transform duration-300 group-hover:scale-[1.03] group-active:scale-95 w-full">
                Reserve This Quote &amp; Schedule
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </button>
            <p className="mt-4 text-center text-xs text-slate-400">
              Estimates only — final quote confirmed before your inspection.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
